import { useState, type ReactNode } from 'react';

import { DataTable, Montant, Nombre } from '../../composants/ui/index.ts';
import type { DetailVente } from './types.ts';

/** Lignes de la vente, marge repliable, et le pied de totaux — RG-046, RG-143. */
export function Articles({ vente }: { vente: DetailVente }): ReactNode {
  // Le coût n'existe dans la charge utile que pour qui a le droit de le voir.
  const avecCouts = vente.lignes.some((ligne) => ligne.cout_unitaire_fige !== undefined);
  const [margeVisible, setMargeVisible] = useState(true);
  const afficherMarge = avecCouts && margeVisible;

  const nbUnites = vente.lignes.reduce((somme, ligne) => somme + ligne.quantite, 0);
  const margeTotale = vente.lignes.reduce((somme, ligne) => somme + (ligne.marge_ligne ?? 0), 0);

  return (
    <section aria-label="Articles vendus" className="vente-section">
      <div className="fiche-table-tete">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h2 style={{ margin: 0 }}>Articles</h2>
          <span className="fiche-compteur">
            {nbUnites} unité{nbUnites > 1 ? 's' : ''} · {vente.lignes.length} ligne{vente.lignes.length > 1 ? 's' : ''}
          </span>
        </div>
        {avecCouts && (
          <button
            type="button"
            className="bouton--discret"
            aria-pressed={margeVisible}
            onClick={() => {
              setMargeVisible((actuel) => !actuel);
            }}
          >
            {margeVisible ? 'Masquer la marge' : 'Afficher la marge'}
          </button>
        )}
      </div>

      <DataTable
        lignes={vente.lignes}
        cleDe={(ligne) => ligne.id}
        vide="Cette vente ne porte aucune ligne."
        colonnes={[
          {
            cle: 'designation',
            entete: 'Produit',
            rendu: (l) => (
              <div>
                <div style={{ fontWeight: 600 }}>{l.designation}</div>
                <div className="fiche-produit__reference">{l.reference}</div>
                {/* Un IMEI par unité vendue — vérifiable en cas de litige SAV. */}
                {(l.imeis ?? [])
                  .filter((paire) => paire.imei !== null || paire.imei2 !== null)
                  .map((paire, index) => (
                    <div key={index} className="fiche-produit__reference">
                      IMEI : {paire.imei ?? ''}
                      {paire.imei2 !== null && paire.imei2 !== '' ? ` / ${paire.imei2}` : ''}
                    </div>
                  ))}
              </div>
            ),
          },
          {
            cle: 'quantite',
            entete: 'Qté',
            numerique: true,
            rendu: (l) => <Nombre valeur={l.quantite} />,
          },
          {
            cle: 'prix',
            entete: 'Prix unitaire',
            numerique: true,
            rendu: (l) => <Montant valeur={l.prix_unitaire} />,
          },
          {
            cle: 'remise',
            entete: 'Remise',
            numerique: true,
            rendu: (l) => (l.remise_ligne > 0 ? <Montant valeur={-l.remise_ligne} /> : '—'),
          },
          ...(afficherMarge
            ? [
                {
                  cle: 'marge',
                  entete: 'Marge',
                  numerique: true,
                  rendu: (l: (typeof vente.lignes)[number]) => (
                    <span
                      style={{
                        background: 'var(--gold-bg)',
                        color: 'var(--gold)',
                        borderRadius: 6,
                        padding: '2px 8px',
                        fontWeight: 600,
                      }}
                    >
                      <Montant valeur={l.marge_ligne ?? null} />
                    </span>
                  ),
                },
              ]
            : []),
          {
            cle: 'total',
            entete: 'Total',
            numerique: true,
            rendu: (l) => <Montant valeur={l.total_ligne} />,
          },
        ]}
      />

      <Totaux vente={vente} />

      {afficherMarge && margeTotale <= 0 && (
        <p className="message message--attention" role="note">
          <strong>Marge nulle sur cette vente.</strong> Vérifiez le prix d’achat des articles ou le prix pratiqué.
          Visible uniquement par les profils autorisés.
        </p>
      )}
    </section>
  );
}

/**
 * Pied de totaux.
 *
 * L'ARRONDI est affiché même à zéro — RG-004. Le client doit pouvoir refaire
 * l'addition et tomber juste ; une ligne absente le laisserait devant un écart
 * de deux francs qu'il ne saurait pas expliquer.
 */
function Totaux({ vente }: { vente: DetailVente }): ReactNode {
  return (
    <div className="pile" style={{ gap: 6, maxWidth: 320, marginLeft: 'auto', marginTop: 12 }}>
      <div className="ligne" style={{ justifyContent: 'space-between' }}>
        <span>Sous-total</span>
        <Montant valeur={vente.sous_total} />
      </div>
      {vente.remise_pied > 0 && (
        <div className="ligne" style={{ justifyContent: 'space-between' }}>
          <span>Remise</span>
          <Montant valeur={-vente.remise_pied} />
        </div>
      )}
      <div className="ligne" style={{ justifyContent: 'space-between' }}>
        <span>Arrondi</span>
        <Montant valeur={vente.arrondi} />
      </div>
      <div
        className="ligne"
        style={{ justifyContent: 'space-between', borderTop: '1px solid var(--trait)', paddingTop: 10, fontWeight: 700 }}
      >
        <span>Total</span>
        <span style={{ fontSize: '1.25rem' }}>
          <Montant valeur={vente.total} total />
        </span>
      </div>
    </div>
  );
}
